import type {
  SubmissionReceiptFields,
  SubmissionReceiptSummary,
} from '@tabbio/types';

/**
 * Submission receipts on screen (audit E03; CONTRACT.md "Submission", I9).
 *
 * The API (E02) adds `receipts` (summaries, newest first, the current one
 * marked) and `receiptsStatus` to job applications and talent network
 * memberships. This module turns those fields into what the "What you sent"
 * section shows, for the candidate and for the employer, on native and web:
 * one builder, so every surface tells the same story.
 *
 * Honesty rules:
 * - keys absent (receipts switched off or unreadable): show nothing, never a
 *   guess;
 * - `historical` (the row predates receipts): say so, with no details;
 * - a field the receipt does not carry reads "Not recorded", never a value
 *   taken from today's CV.
 *
 * Employers see summaries only: a receipt never carries the document, and no
 * surface links an earlier receipt to one (owner default from #609).
 */

export type SubmissionReceiptAudience = 'candidate' | 'employer';

/** Every copy key this module hands to a renderer (common namespace). */
export const SUBMISSION_RECEIPT_COPY_KEYS = [
  'submissionReceipts.candidateTitle',
  'submissionReceipts.employerTitle',
  'submissionReceipts.sentCount',
  'submissionReceipts.recipient',
  'submissionReceipts.fields.cv',
  'submissionReceipts.fields.language',
  'submissionReceipts.fields.sentAt',
  'submissionReceipts.fields.source',
  'submissionReceipts.fields.file',
  'submissionReceipts.heading.original',
  'submissionReceipts.heading.updated',
  'submissionReceipts.current',
  'submissionReceipts.cv.none',
  'submissionReceipts.cv.notRecorded',
  'submissionReceipts.language.en',
  'submissionReceipts.language.ar',
  'submissionReceipts.language.notRecorded',
  'submissionReceipts.source.app',
  'submissionReceipts.source.companyPage',
  'submissionReceipts.source.publicApply',
  'submissionReceipts.source.other',
  'submissionReceipts.historical.title',
  'submissionReceipts.historical.body',
  'submissionReceipts.employerSummaryNote',
] as const;

export type SubmissionReceiptCopyKey =
  (typeof SUBMISSION_RECEIPT_COPY_KEYS)[number];

/** The CV language as sent: a translated name, or the code itself. */
export type SubmissionReceiptLanguage =
  | { key: SubmissionReceiptCopyKey }
  | { text: string };

export interface SubmissionReceiptEntry {
  id: string;
  sequence: number;
  /** The submission the recipient holds now. */
  current: boolean;
  /**
   * "Original submission" / "Updated submission" when the row was sent more
   * than once; null for a single submission (nothing to tell apart).
   */
  headingKey: SubmissionReceiptCopyKey | null;
  submittedAt: string;
  /** The CV title exactly as sent (its title then, not now). */
  cvTitle: string | null;
  /** Shown in place of the title when there is none to show. */
  cvFallbackKey: SubmissionReceiptCopyKey | null;
  language: SubmissionReceiptLanguage;
  sourceKey: SubmissionReceiptCopyKey;
  /** An uploaded resume sent with this submission (public apply). */
  resumeFilename: string | null;
}

/** Props of the receipts section (native and web twins). */
export interface SubmissionReceiptsSectionProps {
  fields: SubmissionReceiptFields | null | undefined;
  audience: SubmissionReceiptAudience;
  /** Candidate only: the company (and job) the submission went to. */
  recipient?: string | null;
}

export type SubmissionReceiptsView =
  | { state: 'unavailable' }
  | { state: 'historical' }
  | { state: 'recorded'; entries: SubmissionReceiptEntry[] };

const SOURCE_KEYS: Record<string, SubmissionReceiptCopyKey> = {
  tabbio_app: 'submissionReceipts.source.app',
  public_page: 'submissionReceipts.source.companyPage',
  public_new_user: 'submissionReceipts.source.publicApply',
};

export function receiptSourceKey(source: string): SubmissionReceiptCopyKey {
  return SOURCE_KEYS[source] ?? 'submissionReceipts.source.other';
}

export function receiptLanguage(
  locale: string | null | undefined,
): SubmissionReceiptLanguage {
  const code = typeof locale === 'string' ? locale.trim() : '';
  if (!code) return { key: 'submissionReceipts.language.notRecorded' };
  const primary = code.split(/[-_]/)[0]?.toLowerCase();
  if (primary === 'en') return { key: 'submissionReceipts.language.en' };
  if (primary === 'ar') return { key: 'submissionReceipts.language.ar' };
  // Not a language the app translates: the code is still the exact fact.
  return { text: code };
}

function cvFallbackKey(
  receipt: SubmissionReceiptSummary,
): SubmissionReceiptCopyKey | null {
  const title = receipt.cv?.title;
  if (typeof title === 'string' && title.trim()) return null;
  // A CV identity without a readable document, or nothing sent at all.
  if (receipt.cv) return 'submissionReceipts.cv.notRecorded';
  return 'submissionReceipts.cv.none';
}

function toEntry(
  receipt: SubmissionReceiptSummary,
  current: boolean,
  several: boolean,
): SubmissionReceiptEntry {
  const title = receipt.cv?.title;
  return {
    id: receipt.id,
    sequence: receipt.sequence,
    current,
    headingKey: several
      ? receipt.sequence <= 1
        ? 'submissionReceipts.heading.original'
        : 'submissionReceipts.heading.updated'
      : null,
    submittedAt: receipt.submittedAt,
    cvTitle: typeof title === 'string' && title.trim() ? title : null,
    cvFallbackKey: cvFallbackKey(receipt),
    language: receiptLanguage(receipt.cv?.locale),
    sourceKey: receiptSourceKey(receipt.source),
    resumeFilename: receipt.resume?.filename?.trim() || null,
  };
}

/**
 * What a receipts section shows for one application or membership. Entries
 * come newest first whatever order the payload used; the current one is the
 * server's `current` mark (the newest when no entry carries it).
 */
export function buildSubmissionReceiptsView(
  fields: SubmissionReceiptFields | null | undefined,
): SubmissionReceiptsView {
  if (!fields) return { state: 'unavailable' };
  const { receipts, receiptsStatus } = fields;
  if (receiptsStatus === 'historical') return { state: 'historical' };
  if (receiptsStatus !== 'recorded' || !Array.isArray(receipts)) {
    return { state: 'unavailable' };
  }
  // "recorded" with nothing in it cannot be shown as a receipt: the honest
  // reading is the same as a row that predates receipts.
  if (receipts.length === 0) return { state: 'historical' };

  const newestFirst = [...receipts].sort((a, b) => b.sequence - a.sequence);
  const currentId =
    newestFirst.find((receipt) => receipt.current)?.id ?? newestFirst[0].id;
  const several = newestFirst.length > 1;
  return {
    state: 'recorded',
    entries: newestFirst.map((receipt) =>
      toEntry(receipt, receipt.id === currentId, several),
    ),
  };
}

export function receiptsSectionTitleKey(
  audience: SubmissionReceiptAudience,
): SubmissionReceiptCopyKey {
  return audience === 'employer'
    ? 'submissionReceipts.employerTitle'
    : 'submissionReceipts.candidateTitle';
}

/**
 * The employer's note under a history: earlier submissions are summaries,
 * only the current document is shared (owner default from #609).
 */
export function receiptsFootnoteKey(
  audience: SubmissionReceiptAudience,
  view: SubmissionReceiptsView,
): SubmissionReceiptCopyKey | null {
  return audience === 'employer' &&
    view.state === 'recorded' &&
    view.entries.length > 1
    ? 'submissionReceipts.employerSummaryNote'
    : null;
}

/** "Sent" time of a receipt, date and time, in the reader's locale. */
export function formatReceiptTime(value: string, locale: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  try {
    return date.toLocaleString(locale, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return date.toISOString();
  }
}
